import {
  cardSubtitle,
  computeSkillMatch,
  criteriaToSkillSplit,
  scoreBgVar,
  scoreColorVar,
  userInitials,
  olderPreScore,
  olderPreScoreLine,
  vorauswahlLine,
  vorauswahlOf,
  type Candidate,
  type Job,
} from "@vidario/domain";
import { Avatar, Button, Card, SkillTag, StatusBadge, cn } from "@vidario/ui";
import { Check, Send, Sparkles } from "lucide-react";
import { type CSSProperties, type ChangeEvent, type MouseEvent, type ReactElement } from "react";

import { CandidateLocation } from "../../anonym/CandidateLocation";
import { useAnonymView, useMaskedCandidate } from "../../anonym/useAnonymView";

export interface CandidateCardProps {
  job: Job;
  candidate: Candidate;
  selected: boolean;
  onToggle: (e: ChangeEvent<HTMLInputElement>) => void;
  onOpen: () => void;
  disabled?: boolean;
  /** Optional quick action — renders "Talent auswählen" on the Longlist
   * tab for candidates whose status is still `longlist`. */
  onMoveToSelected?: () => void;
  /** Loading state for the "Talent auswählen" button while the mutation
   * is in flight — guards against double submits. */
  moveInProgress?: boolean;
  /** Optional quick action — renders "Outreach starten" on the
   * Ausgewählte-Talente tab for candidates whose status is `selected`. */
  onSendToOutreach?: () => void;
  /** Loading state for the "Outreach starten" button. */
  outreachInProgress?: boolean;
}

/** Longlist candidate card (MVP §4 Screen 3). Checkbox + avatar + name,
 * title/company, location, skill-match bar + tags, status badge, Details
 * button. Rejected candidates render dimmed with the rejection reason
 * inline. */
export function CandidateCard({
  job,
  candidate,
  selected,
  onToggle,
  onOpen,
  disabled,
  onMoveToSelected,
  moveInProgress = false,
  onSendToOutreach,
  outreachInProgress = false,
}: CandidateCardProps): ReactElement {
  // In the Anonym-Ansicht (#140) the card shows the masked candidate: code, class, no reason.
  const anonym = useAnonymView();
  const { candidate: c } = useMaskedCandidate(candidate);
  const isRejected = c.status === "rejected";
  // Only the parts that exist; the Anonym-Ansicht shows no title, just the class (#163).
  const subtitle = cardSubtitle(c.currentTitle, c.currentCompany);
  const { mustHaves, shouldHaves } = criteriaToSkillSplit(job.criteria);
  const match = computeSkillMatch(c, mustHaves, shouldHaves);
  // Up to 6 skill tags: must-haves first (met + missing), then 2 should-met
  // for variety. Mirrors the prototype's "6 tags, Must-Haves highlighted".
  const skills: readonly { type: Parameters<typeof SkillTag>[0]["type"]; label: string }[] = [
    ...match.mustHit.map((label) => ({ type: "must-met" as const, label })),
    ...match.mustMiss.map((label) => ({ type: "must-missing" as const, label })),
    ...match.shouldHit.slice(0, 2).map((label) => ({ type: "should-met" as const, label })),
  ].slice(0, 6);

  // A sorted-out row's score is the Vorauswahl's or an older algorithmic one,
  // not an AI Match-Score (#135, #186).
  const vorauswahl = vorauswahlOf(c);
  const priorScore = olderPreScore(c);
  const hasScore = c.score != null && !isRejected && vorauswahl == null && priorScore == null;
  const stopProp = (e: MouseEvent): void => e.stopPropagation();

  return (
    <Card
      interactive
      onClick={onOpen}
      className={cn("flex h-full flex-col p-5", isRejected && "bg-neutral-50 opacity-55")}
      padded={false}
      data-testid={`candidate-card-${c.id}`}
    >
      <div className="flex items-start gap-3.5">
        <input
          type="checkbox"
          checked={selected}
          disabled={disabled}
          onChange={onToggle}
          onClick={stopProp}
          aria-label={`Kandidat ${c.name} auswählen`}
          className="mt-1 h-4 w-4 cursor-pointer accent-primary-500 disabled:cursor-default"
        />
        {/* On phones the initials give their width to the name. */}
        <Avatar
          initials={anonym ? "" : userInitials(c.name)}
          variant={anonym ? "placeholder" : "initials"}
          size={44}
          className="max-sm:hidden"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div
              data-testid="candidate-name-row"
              className="flex min-w-0 flex-wrap items-center gap-2"
            >
              <h4
                className={cn(
                  "m-0 min-w-0 text-[15px] font-semibold -tracking-[0.2px] wrap-anywhere",
                  anonym && "font-mono",
                )}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen();
                  }}
                  className="inline cursor-pointer bg-transparent p-0 text-left font-inherit text-inherit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-0"
                  aria-label={`Kandidat öffnen: ${c.name}`}
                >
                  {c.name}
                </button>
              </h4>
              <span className="rounded-sm bg-neutral-100 px-1.5 py-0.5 text-[11px] font-medium text-neutral-700">
                {c.currentTitle?.includes("Staff")
                  ? "Staff"
                  : c.currentTitle?.includes("Principal")
                    ? "Principal"
                    : "Senior"}
              </span>
            </div>
            {hasScore && (
              <span
                data-testid="candidate-score"
                className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
                style={{
                  background:
                    c.score! >= 85
                      ? "var(--color-success-50)"
                      : c.score! >= 70
                        ? "var(--color-warning-50)"
                        : "var(--color-neutral-100)",
                  color: scoreColorVar(c.score!),
                }}
              >
                <Sparkles size={10} strokeWidth={2.5} />
                {c.score}%
              </span>
            )}
          </div>
          {subtitle && (
            <div className="mt-0.5 text-[13px] text-text-secondary wrap-anywhere">{subtitle}</div>
          )}
          <CandidateLocation
            className="mt-1.5"
            location={c.location}
            distanceKm={c.distanceKm}
            jobLocation={job.location}
            anonym={anonym}
          />
        </div>
      </div>

      {hasScore && (
        <div className="mt-3.5">
          <div className="mb-1 flex justify-between text-[11px] font-medium text-text-tertiary">
            <span>Skill-Match</span>
            <span>
              {match.mustHit.length}/{match.mustHit.length + match.mustMiss.length} Must-Have
            </span>
          </div>
          <div className="h-1 overflow-hidden rounded-[2px] bg-neutral-100">
            <div
              aria-hidden="true"
              className="h-full transition-[width] duration-400"
              style={
                {
                  width: `${c.score!}%`,
                  background: scoreBgVar(c.score!),
                } satisfies CSSProperties
              }
            />
          </div>
        </div>
      )}

      {vorauswahl && (
        <div
          data-testid="candidate-vorauswahl"
          className="mt-3.5 text-[11px] font-medium text-text-tertiary"
        >
          {vorauswahlLine(vorauswahl)}
        </div>
      )}

      {priorScore != null && (
        <div
          data-testid="candidate-prior-score"
          className="mt-3.5 text-[11px] font-medium text-text-tertiary"
        >
          {olderPreScoreLine(priorScore)}
        </div>
      )}

      {isRejected && (c.rejectionReason || anonym) && (
        <div className="mt-3 rounded-md bg-danger-50 px-2.5 py-2 text-xs leading-snug text-danger-700">
          {anonym ? (
            <b>Abgelehnt</b>
          ) : (
            <>
              <b>Abgelehnt:</b> {c.rejectionReason}
            </>
          )}
        </div>
      )}

      {!isRejected && skills.length > 0 && (
        <div className="mt-3.5 flex flex-wrap gap-1.5">
          {skills.map((s) => (
            <SkillTag key={`${s.type}-${s.label}`} type={s.type} label={s.label} />
          ))}
        </div>
      )}

      <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-4">
        <StatusBadge status={c.status} size="sm" />
        {onMoveToSelected && (
          <Button
            variant="secondary"
            size="sm"
            className="ml-auto"
            leftIcon={<Check size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              onMoveToSelected();
            }}
            loading={moveInProgress}
            data-testid={`candidate-move-selected-${c.id}`}
          >
            Talent auswählen
          </Button>
        )}
        {onSendToOutreach && (
          <Button
            variant="secondary"
            size="sm"
            className="ml-auto"
            leftIcon={<Send size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              onSendToOutreach();
            }}
            loading={outreachInProgress}
            data-testid={`candidate-send-outreach-${c.id}`}
          >
            Outreach starten
          </Button>
        )}
      </div>
    </Card>
  );
}
